/**
 * Platform demo tour videos for the landing page (YouTube embeds).
 *
 * Set REACT_APP_DEMO_VIDEO_YOUTUBE_IDS after uploading exports to YouTube:
 *   platform-overview:VIDEO_ID,candidates:VIDEO_ID,interviewers:VIDEO_ID,...
 *
 * @see scripts/demo-studio/YOUTUBE_PUBLISH.md
 */

function parseYoutubeIdMap() {
  const raw = (process.env.REACT_APP_DEMO_VIDEO_YOUTUBE_IDS || '')
    .replace(/^["']|["']$/g, '')
    .trim();
  if (!raw) return {};

  return Object.fromEntries(
    raw
      .split(',')
      .map((pair) => {
        const colon = pair.indexOf(':');
        if (colon === -1) return null;
        const id = pair.slice(0, colon).trim();
        const videoId = pair.slice(colon + 1).trim();
        return id && videoId ? [id, videoId] : null;
      })
      .filter(Boolean)
  );
}

const YOUTUBE_ID_MAP = parseYoutubeIdMap();

/** @type {{ id: string, tabLabel: string, title: string, description: string, durationLabel: string }[]} */
export const PLATFORM_DEMO_VIDEOS_BASE = [
  {
    id: 'platform-overview',
    tabLabel: 'Platform overview',
    title: 'Platform Overview',
    description:
      'A five-minute tour of JobMockAI for candidates, interviewers, and recruiters — from landing page to dashboard.',
    durationLabel: '~5 min',
  },
  {
    id: 'candidates',
    tabLabel: 'For candidates',
    title: 'For Candidates',
    description:
      'How job seekers practice AI mock interviews, track progress, and prepare for real hiring conversations.',
    durationLabel: '~5 min',
  },
  {
    id: 'interviewers',
    tabLabel: 'For interviewers',
    title: 'For Interviewers',
    description:
      'How founding interviewers apply, get registered by admins, and launch AI-assisted marketplace listings for paid mock sessions.',
    durationLabel: '~8 min',
  },
  {
    id: 'recruiters',
    tabLabel: 'For recruiters',
    title: 'For Recruiters',
    description:
      'How hiring teams post jobs, review applicants, and manage pipelines on one platform.',
    durationLabel: '~5 min',
  },
  {
    id: 'interview-recording',
    tabLabel: 'Interview recording',
    title: 'Interview Start + Local Recording',
    description:
      'Starting a mock interview session and capturing a local recording for review.',
    durationLabel: '~7 min',
  },
  {
    id: 'hiring-marketplace',
    tabLabel: 'Hiring marketplace',
    title: 'Hiring Marketplace',
    description:
      'How candidates discover and book expert mock interviews from the hiring marketplace.',
    durationLabel: '~6 min',
  },
  {
    id: 'interviewer-marketplace',
    tabLabel: 'Interviewer marketplace',
    title: 'Interviewer Marketplace',
    description:
      'How interviewers showcase expertise, manage listings, and grow their coaching practice.',
    durationLabel: '~6 min',
  },
  {
    id: 'interview-integrity',
    tabLabel: 'Interviewer integrity',
    title: 'Interviewer Integrity',
    description:
      'Host view on My Bookings: You hosted, live ID show (no upload), identity attestation checklist, Integrity evidence on upcoming and past sessions — without auto-fail.',
    durationLabel: '~2.5 min',
  },
  {
    id: 'ai-interview-profiles',
    tabLabel: 'AI interview profiles',
    title: 'Publish AI Interview Profiles',
    description:
      'How interviewers and admins publish AI marketplace listings, and how recruiters publish AI interview templates for applicants.',
    durationLabel: '~8 min',
  },
];

export const PLATFORM_DEMO_VIDEOS = PLATFORM_DEMO_VIDEOS_BASE.map((demo) => ({
  ...demo,
  youtubeVideoId: YOUTUBE_ID_MAP[demo.id] || null,
}));

export const PLATFORM_DEMO_SECTION = {
  id: 'platform-demo-videos',
  title: 'See JobMockAI in action',
  subtitle:
    'Watch short product tours for each audience — hosted on YouTube for fast playback worldwide.',
};

export const PLATFORM_DEMO_TEASER = {
  title: PLATFORM_DEMO_SECTION.title,
  subtitle:
    'Short video tours for candidates, recruiters, and interviewers — see the platform before you sign up.',
  cta: { label: 'Watch product tours', to: '/product-tours', variant: 'candidate' },
};

export const PRODUCT_TOURS_PAGE = {
  title: 'JobMockAI Product Tours | Video Walkthroughs',
  description:
    'Watch JobMockAI product tours for candidates, recruiters, and interviewers. Explore mock interviews, hiring tools, and marketplace features on YouTube.',
  canonicalPath: '/product-tours',
};

export function getPlatformDemoTabIndex(tabId) {
  if (!tabId) return 0;
  const index = PLATFORM_DEMO_VIDEOS.findIndex((demo) => demo.id === tabId);
  return index >= 0 ? index : 0;
}

export const PLATFORM_DEMO_CTA = {
  headline: 'Ready to try it yourself?',
  primary: { label: 'Get Started Free', to: '/signup', variant: 'candidate' },
  secondary: { label: 'Start Practicing', to: '/interview/practice', variant: 'secondary' },
};

export function hasAnyPlatformDemoVideo() {
  return PLATFORM_DEMO_VIDEOS.some((demo) => demo.youtubeVideoId);
}
